import { addressFor, type AddressWords } from './address';
import { segments, type Segment } from './segments';
import type { Kin, KinPack, Sex } from './types';

/**
 * Spanish relationship words, following the genealogy tables a Spanish-speaking user sent (tabla de
 * parentesco, árbol genealógico). Collateral relatives are named by the generations up to the shared
 * ancestor on each side (PRD 7.2.1):
 *   same generation  → primo hermano, primo segundo, primo tercero …
 *   older line       → tío / tío abuelo / tío bisabuelo … + segundo, tercero … (from their side)
 *   younger line     → sobrino / sobrino nieto / sobrino bisnieto … + segundo, tercero … (from my side)
 */

/** A masculine and a feminine form, chosen at the end by the person's sex. */
interface Pair {
  m: string;
  f: string;
}

/** Feminine by swapping each word's final o for a ("tío abuelo segundo" → "tía abuela segunda"). */
const fem = (m: string) => m.replace(/o\b/g, 'a');
const P = (m: string, f = fem(m)): Pair => ({ m, f });
const join = (...ps: Pair[]): Pair => ({ m: ps.map((p) => p.m).join(' '), f: ps.map((p) => p.f).join(' ') });

/** "Tío/a segundo/a" when the sex is unknown, word by word. */
function neutral(p: Pair): string {
  const ms = p.m.split(' ');
  const fs = p.f.split(' ');
  if (ms.length !== fs.length) return `${p.m}/${p.f}`;
  return ms
    .map((m, i) => {
      const f = fs[i]!;
      if (m === f) return m;
      if (f === m.slice(0, -1) + 'a') return `${m}/a`;
      if (f === m + 'a') return `${m}/a`;
      return `${m}/${f}`;
    })
    .join(' ');
}

function say(p: Pair, sex: Sex): string {
  return sex === 'f' ? p.f : sex === 'm' ? p.m : neutral(p);
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const UNITS = ['', 'primero', 'segundo', 'tercero', 'cuarto', 'quinto', 'sexto', 'séptimo', 'octavo', 'noveno'];
const TENS = ['', 'décimo', 'vigésimo', 'trigésimo', 'cuadragésimo', 'quincuagésimo', 'sexagésimo', 'septuagésimo', 'octogésimo', 'nonagésimo'];
const TEENS: Record<number, string> = {
  10: 'décimo',
  11: 'undécimo',
  12: 'duodécimo',
  13: 'decimotercero',
  14: 'decimocuarto',
  15: 'decimoquinto',
  16: 'decimosexto',
  17: 'decimoséptimo',
  18: 'decimoctavo',
  19: 'decimonoveno',
};

/** Masculine ordinal as it follows a noun: "primo tercero", "tío abuelo segundo". */
export function ordinalEs(n: number): string {
  if (n < 10) return UNITS[n]!;
  if (n < 20) return TEENS[n]!;
  if (n < 100) {
    const u = n % 10;
    return TENS[Math.floor(n / 10)]! + (u ? ` ${UNITS[u]}` : '');
  }
  return `${n}.º`;
}

/** Ordinal before a masculine noun drops a final -o of primero and tercero ("decimotercer abuelo"). */
const before = (n: number): Pair => {
  const m = ordinalEs(n);
  return { m: m.replace(/(primer|tercer)o$/, '$1'), f: fem(m) };
};

const ANCESTORS = ['', 'padre', 'abuelo', 'bisabuelo', 'tatarabuelo', 'trastatarabuelo', 'pentabuelo', 'hexabuelo', 'heptabuelo', 'octabuelo', 'nonabuelo', 'decabuelo'];
const DESCENDANTS = ['', 'hijo', 'nieto', 'bisnieto', 'tataranieto', 'trastataranieto', 'pentanieto', 'hexanieto', 'heptanieto', 'octanieto', 'nonanieto', 'decanieto'];

/** Ancestor d generations up: padre, abuelo, bisabuelo … decabuelo (11), then undécimo abuelo (12) and on. */
function ancestor(d: number): Pair {
  if (d === 1) return { m: 'padre', f: 'madre' };
  if (d < ANCESTORS.length) return P(ANCESTORS[d]!);
  return join(before(d - 1), P('abuelo'));
}

/** Descendant d generations down: hijo, nieto, bisnieto, tataranieto, trastataranieto … */
function descendant(d: number): Pair {
  if (d < DESCENDANTS.length) return P(DESCENDANTS[d]!);
  return join(before(d - 1), P('nieto'));
}

/** Plural of the ancestor word, for the generation rail: "Bisabuelos", "Undécimos abuelos". */
function ancestorsPlural(d: number): string {
  if (d === 1) return 'padres';
  if (d < ANCESTORS.length) return `${ANCESTORS[d]}s`;
  return `${ordinalEs(d - 1).split(' ').map((w) => `${w}s`).join(' ')} abuelos`;
}

function descendantsPlural(d: number): string {
  if (d < DESCENDANTS.length) return `${DESCENDANTS[d]}s`;
  return `${ordinalEs(d - 1).split(' ').map((w) => `${w}s`).join(' ')} nietos`;
}

const tio = (k: number): Pair => (k === 1 ? P('tío') : join(P('tío'), ancestor(k)));
const sobrino = (k: number): Pair => (k === 1 ? P('sobrino') : join(P('sobrino'), descendant(k)));
const politico = P('político');
/** -astro words for step relations: abuelastro, nietastro, bisabuelastra. */
const astro = (p: Pair): Pair => ({ m: p.m.replace(/o$/, 'astro'), f: p.f.replace(/a$/, 'astra') });

/** Blood relatives who are neither ancestors nor descendants, from the generations on each side. */
function collateral(a: number, b: number, half: boolean, byMarriage = false): Pair {
  if (a === 1 && b === 1) return half ? { m: 'medio hermano', f: 'media hermana' } : P('hermano');
  if (a === b) {
    // By marriage the everyday "primo político" reads better than "primo hermano político".
    if (a === 2) return byMarriage ? P('primo') : join(P('primo'), P('hermano'));
    return join(P('primo'), P(ordinalEs(a - 1)));
  }
  if (a > b) return b === 1 ? tio(a - b) : join(tio(a - b), P(ordinalEs(b)));
  return a === 1 ? sobrino(b - a) : join(sobrino(b - a), P(ordinalEs(a)));
}

function wordFor(k: Kin): Pair | null {
  switch (k.kind) {
    case 'none':
      return null;
    case 'self':
      return { m: 'tú', f: 'tú' };
    case 'spouse':
      return { m: 'esposo', f: 'esposa' };
    case 'stepSibling':
      return P('hermanastro');
    case 'coParentInLaw':
      return P('consuegro');
    case 'relative':
      return { m: 'pariente', f: 'pariente' };
    case 'blood':
    case 'marriage':
      break;
  }
  const s = k.shape!;
  const via = k.via;
  if ('up' in s) {
    if (via === 'blood') return ancestor(s.up);
    if (via === 'mine') return s.up === 1 ? P('suegro') : join(ancestor(s.up), politico);
    return s.up === 1 ? { m: 'padrastro', f: 'madrastra' } : astro(ancestor(s.up));
  }
  if ('down' in s) {
    if (via === 'blood') return descendant(s.down);
    if (via === 'mine') return astro(descendant(s.down));
    return s.down === 1 ? { m: 'yerno', f: 'nuera' } : join(descendant(s.down), politico);
  }
  if (via === 'blood') return collateral(s.a, s.b, k.half);
  if (s.a === 1 && s.b === 1) return via === 'both' ? P('concuñado') : P('cuñado');
  return join(collateral(s.a, s.b, false, true), politico);
}

function noun(s: Segment): Pair {
  switch (s.rel) {
    case 'up':
      return ancestor(s.depth);
    case 'down':
      return descendant(s.depth);
    case 'sibling':
      return s.half ? { m: 'medio hermano', f: 'media hermana' } : P('hermano');
    case 'spouse':
      return { m: 'esposo', f: 'esposa' };
  }
}

export const esPack: KinPack = {
  id: 'es',
  name: 'Español',
  lang: 'es',
  dir: 'ltr',
  status: 'beta',
  label(k: Kin): string {
    if (k.kind === 'spouse' && k.sex === null) return 'Cónyuge';
    const w = wordFor(k);
    return w ? cap(say(w, k.sex)) : '';
  },
  generation(delta: number): string {
    if (delta === 0) return 'Tu generación';
    return cap(delta < 0 ? ancestorsPlural(-delta) : descendantsPlural(delta));
  },
  /** "Hijo del hermano de tu padre": Spanish names the far end first. */
  explain(k: Kin): string {
    const segs = segments(k.path);
    if (segs.length < 2) return '';
    const last = segs.length - 1;
    let out = cap(say(noun(segs[last]!), segs[last]!.sex));
    for (let i = last - 1; i >= 0; i--) {
      const s = segs[i]!;
      const word = s.rel === 'spouse' && s.sex === null ? 'cónyuge' : say(noun(s), s.sex);
      out += i === 0 ? ` de tu ${word}` : s.sex === 'f' ? ` de la ${word}` : ` del ${word}`;
    }
    return out;
  },
  address: (k, name) => addressFor(ES_ADDRESS, k, name),
};

/** Spanish forms of address (PRD 7.2.4): tío and tía with the name, don and doña for parents-in-law. */
const ES_ADDRESS: AddressWords = {
  parent: { m: ['Papá', 'Papi'], f: ['Mamá', 'Mami'] },
  grandparent: (_side, sex, great) => (great ? (sex === 'f' ? ['Bisabuela'] : ['Bisabuelo']) : sex === 'f' ? ['Abuela', 'Abuelita'] : ['Abuelo', 'Abuelito']),
  grandUncle: { m: ['Tío {name}', 'Tío'], f: ['Tía {name}', 'Tía'] },
  elder: { m: [], f: [] },
  uncle: {
    FB: { any: ['Tío {name}', 'Tío'] },
    FZ: { any: ['Tía {name}', 'Tía'] },
    MB: { any: ['Tío {name}', 'Tío'] },
    MZ: { any: ['Tía {name}', 'Tía'] },
  },
  uncleSpouse: {
    FB: { any: ['Tía {name}', 'Tía'] },
    FZ: { any: ['Tío {name}', 'Tío'] },
    MB: { any: ['Tía {name}', 'Tía'] },
    MZ: { any: ['Tío {name}', 'Tío'] },
  },
  parentInLaw: { m: ['Suegro', 'Don {name}'], f: ['Suegra', 'Doña {name}'] },
  elderInLaw: (sex) => (sex === 'f' ? ['Cuñada'] : sex === 'm' ? ['Cuñado'] : ['Cuñado', 'Cuñada']),
  elderSiblingSpouse: { m: ['Cuñado'], f: ['Cuñada'] },
  // Cuñado has no age split (DEM, "¡Hola cuñado!, saludó"); a younger sibling's spouse is cuñado too.
  siblingInLaw: { m: ['Cuñado'], f: ['Cuñada'] },
  coParentInLaw: { m: ['Consuegro'], f: ['Consuegra'] },
};
